import Grid from '@mui/material/Grid';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { PageSection } from '@openx/components-core/lib/PageSection/PageSection.tsx';
import {
  type OrganizationData,
  type OrganizationsList,
  type OrganizationType,
  organizationType,
} from '@openx/types/organization.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllowedCountriesLabels, getOrganizationsLabels, organizationFields } from '../../../constants.ts';
import { AccountHierarchy } from '../components/AccountHierarchy.tsx';
import { DspRestrictionReadOnlySection } from './DspRestrictionReadOnlySection.tsx';

type OrganizationDetailsProps = {
  organization: OrganizationData;
  parentOrganizationsData: OrganizationsList[];
  parentOrganizationsLoading: boolean;
};

export const DetailsSection = ({
  organization,
  parentOrganizationsData,
  parentOrganizationsLoading,
}: OrganizationDetailsProps) => {
  const { t } = useTranslation();
  const organizationLabels = getOrganizationsLabels(t);
  const mapOfAllowedCountries = getAllowedCountriesLabels(t);
  const allowedCountries = useMemo(
    () =>
      organization.allowed_countries?.map((country: string) => mapOfAllowedCountries[country] || country).join(', '),
    [mapOfAllowedCountries, organization.allowed_countries]
  );

  const canViewDspRestrictions = (
    [organizationType.Buyer, organizationType.TechPartner] as OrganizationType[]
  ).includes(organization.organization_type as OrganizationType);

  return (
    <PageSection divider title={t('Organization Details')}>
      <Grid size={5}>
        <FieldDetails title={organizationLabels[organizationFields.ID]} value={organization.id} />
        <FieldDetails
          title={organizationLabels[organizationFields.PARENT_ACCOUNT_ID]}
          value={organization.parent_account?.id}
        />
        <FieldDetails
          title={organizationLabels[organizationFields.PARENT_ACCOUNT_NAME]}
          value={organization.parent_account?.name}
        />
        <FieldDetails
          title={organizationLabels[organizationFields.ALLOWED_DOMAINS]}
          value={organization.allowed_domains}
        />
        <FieldDetails title={organizationLabels[organizationFields.ALLOWED_COUNTRIES]} value={allowedCountries} />
        <FieldDetails
          title={organizationLabels[organizationFields.ORGANIZATION_TYPE]}
          value={organization.organization_type}
        />
        <FieldDetails title={organizationLabels[organizationFields.DESCRIPTION]} value={organization.description} />

        {canViewDspRestrictions && organization.dsp_restriction && organization.dsp_restriction.length > 0 && (
          <DspRestrictionReadOnlySection
            restrictions={organization.dsp_restriction}
            mode={organization.dsp_restriction_mode}
          />
        )}
      </Grid>

      <Grid size={4}>
        {parentOrganizationsLoading ? (
          <Loader />
        ) : (
          <AccountHierarchy parentOrganizationsData={parentOrganizationsData} organization={organization} />
        )}
      </Grid>
    </PageSection>
  );
};
